import { alpha, useTheme } from '@mui/material';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { ReactComponent as EditFilledIcon } from '../../../design-system/Icons/EditFilled.svg';
import DSBox from '../../../design-system/DSBox';
import { DSTypography } from '../../../design-system/Typography';
import DSAvatar from '../../../design-system/DSAvatar';
import DSButton from '../../../design-system/DSButton';
import { DRAFT, KLYDO_YELLOW } from '../../../consts/colors';
import { SOCIAL_NETWORKS } from '../../../types/socialMedia';
import { SocialMedia } from '../../../api/user';
import { COVER_ASPECT_RATIO, PRIMARY_BUTTON_HOVER_COLOR } from '../consts';
import { getScaledCloudinaryUrl } from '../../../utils';

const ensureAbsoluteUrl = (url: string): string => {
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
};

const openExternalLink = (url: string) =>
  window.open(ensureAbsoluteUrl(url), '_blank', 'noopener');

const SOCIAL_LINK_HOVER_SX = {
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  transition: 'all 0.2s',
  '&:hover': { opacity: 0.7 },
  '&:hover .custom-link-label': { color: KLYDO_YELLOW },
};

type ArtistIdentityCardProps = {
  name: string;
  shortDescription: string;
  photoUrl: string;
  coverUrl: string;
  description: string;
  countryName: string | undefined;
  socialMedia: SocialMedia[];
  coverBackground: string | undefined;
  onEdit: () => void;
};

const ArtistIdentityCard = ({
  name,
  shortDescription,
  photoUrl,
  coverUrl,
  description,
  countryName,
  socialMedia,
  coverBackground,
  onEdit,
}: ArtistIdentityCardProps) => {
  const { spacing, palette } = useTheme();

  return (
    <DSBox
      sx={{
        background: 'linear-gradient(to top, #111111 38%, #212121 100%)',
        borderRadius: spacing(2),
        overflow: 'hidden',
      }}
    >
      <DSBox
        sx={{
          position: 'relative',
          aspectRatio: COVER_ASPECT_RATIO,
          background: coverBackground,
          overflow: 'hidden',
        }}
      >
        {coverUrl && (
          <img
            src={getScaledCloudinaryUrl(coverUrl, 1000)}
            alt="Cover"
            loading="eager"
            decoding="async"
            fetchPriority="high"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        )}
        <DSButton
          variant="contained"
          size="small"
          onClick={onEdit}
          sx={{
            position: 'absolute',
            top: spacing(4),
            right: spacing(4),
            px: '16px',
            py: '12px',
            fontFamily: 'Gilroy-Bold',
            fontSize: '16px',
            fontWeight: 400,
            '&:hover': {
              backgroundColor: PRIMARY_BUTTON_HOVER_COLOR,
              boxShadow: 'none',
            },
          }}
        >
          <DSBox
            component={EditFilledIcon}
            sx={{
              width: 22,
              height: 22,
              '& path': {
                stroke: palette.secondary.dark,
                strokeOpacity: 1,
              },
            }}
          />
          Edit
        </DSButton>
      </DSBox>

      {/* Card content below the cover */}
      <DSBox
        sx={{
          background: 'transparent',
          pb: spacing(5),
          px: { xxs: spacing(3), sm: spacing(6), md: spacing(15) },
        }}
      >
        {/* Avatar */}
        <DSBox
          sx={{
            mt: spacing(-8),
            ml: { xxs: 0, md: spacing(-3) },
            display: 'flex',
          }}
        >
          <DSAvatar
            src={photoUrl ? getScaledCloudinaryUrl(photoUrl, 240) : undefined}
            alt={name}
            sx={{
              width: spacing(15),
              height: spacing(15),
              boxShadow: `0 4px 12px ${alpha(palette.common.black, 0.4)}`,
            }}
          />
        </DSBox>

        {/* Content */}
        <DSBox sx={{ pt: spacing(2) }}>
          {name && (
            <DSTypography variant="h3" color="common.white">
              {name}
            </DSTypography>
          )}
          {shortDescription && (
            <DSTypography
              variant="body1"
              color="common.white"
              sx={{ mt: spacing(1) }}
            >
              {shortDescription}
            </DSTypography>
          )}
          {countryName && (
            <DSBox
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing(0.5),
                mt: spacing(1),
              }}
            >
              <LocationOnOutlinedIcon sx={{ color: DRAFT, fontSize: 16 }} />
              <DSTypography variant="body2" color="text.secondary">
                {countryName}
              </DSTypography>
            </DSBox>
          )}

          {/* Full Bio */}
          {description && (
            <DSBox sx={{ mt: spacing(3) }}>
              <DSTypography variant="body2" color="text.secondary">
                Full Bio
              </DSTypography>
              <DSTypography
                variant="body1"
                color="common.white"
                sx={{ mt: spacing(1) }}
              >
                {description}
              </DSTypography>
            </DSBox>
          )}

          {/* Online Presence */}
          {socialMedia?.length > 0 && (
            <DSBox sx={{ mt: spacing(3) }}>
              <DSTypography variant="body2" color="text.secondary">
                Online Presence
              </DSTypography>
              <DSBox
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing(1.5),
                  mt: spacing(1),
                }}
              >
                {socialMedia.map((item) => {
                  const socialNetwork = SOCIAL_NETWORKS.find(
                    (network) =>
                      network.name.toLowerCase() === item.network.toLowerCase(),
                  );

                  if (socialNetwork) {
                    const IconComponent = socialNetwork.icon;
                    return (
                      <DSBox
                        key={item.network}
                        onClick={() => openExternalLink(item.link)}
                        sx={SOCIAL_LINK_HOVER_SX}
                      >
                        <IconComponent
                          color={palette.text.secondary}
                          width="24px"
                          height="24px"
                        />
                      </DSBox>
                    );
                  }

                  return (
                    <DSBox
                      key={item.network}
                      onClick={() => openExternalLink(item.link)}
                      sx={{
                        ...SOCIAL_LINK_HOVER_SX,
                        gap: spacing(0.5),
                      }}
                    >
                      <LinkOutlinedIcon
                        sx={{ color: 'text.secondary', fontSize: 24 }}
                      />
                      <DSTypography
                        className="custom-link-label"
                        variant="body2"
                        color="text.secondary"
                        sx={{ transition: 'color 0.2s' }}
                      >
                        {item.network}
                      </DSTypography>
                    </DSBox>
                  );
                })}
              </DSBox>
            </DSBox>
          )}
        </DSBox>
      </DSBox>
    </DSBox>
  );
};

export default ArtistIdentityCard;
